// Universal E-E-A-T (Experience, Expertise, Authoritativeness, Trustworthiness)
// configuration — the single source of truth for author and publisher info
// across the entire site.
//
// Positioning is deliberately AUTHENTIC. Galvin is a software engineer who
// builds FinanceLives's tools and researches PRIMARY government/regulator sources
// to encode the rules. We make no claim of professional financial licensing
// (no CFP/CFA/CPA) — for a YMYL site, fabricating credentials is a quality and
// legal risk. Instead, trust is earned the way Google's guidelines reward when
// formal credentials are absent: transparent authorship, primary-source
// citations, and a documented, dated review process.

export const SITE_AUTHOR = {
  name: "Galvin Mendonca",
  credentials: "Software Engineer & Founder",
  jobTitle: "Founder and Tool Developer, FinanceLives",
  // Longer bio used on the author page and Person schema.
  bio:
    "Galvin Mendonca is a software engineer and the founder of FinanceLives. He maintains the site's educational tools and editorial direction. Research, drafting, calculations and images may use software assistance; readers can inspect linked primary sources, stated assumptions and review dates, and report an error for correction. He does not claim a financial-adviser licence.",
  // Short one-liner for compact bylines and meta descriptions.
  shortBio:
    "Software engineer and founder of FinanceLives. Maintains educational finance tools and source-linked guides.",
  url: "https://financelives.com/authors/galvin-mendonca",
  image: "https://financelives.com/author.png",
  // Real, monitored contact — used for corrections and as a publisher contact point.
  email: "contact@financelives.com",
  // Real profiles only. Never fabricate profiles for schema `sameAs`.
  // (The former X/Twitter account was deleted — a dead profile link is a
  // trust liability, so only the live LinkedIn profile remains.)
  sameAs: [
    "https://www.linkedin.com/in/galvin-russell-mendonca-952493176",
  ],
  knowsAbout: [
    "Personal Finance",
    "Tax Planning",
    "Retirement Planning",
    "Mortgage Analysis",
    "Investment Strategy",
    "Financial Calculators",
    "Multi-Country Finance",
    "Financial Literacy",
  ],
};

// Single source of truth for the human "last reviewed" label shown on
// E-E-A-T badges and Sources blocks site-wide. Bump this ONE constant after
// each content review sweep — scattered hardcoded month strings silently went
// stale on hundreds of pages.
export const LAST_REVIEWED = "July 2026";

// Every indexed glossary term and country guide received a substantive
// source/content review in September 2026. This is an editorial review label,
// not a certification that future rules cannot change.
export const GLOSSARY_LAST_REVIEWED = "September 2026";

// Pages added after the last site-wide review need their own truthful label.
// Existing pages keep LAST_REVIEWED until a substantive review reaches them.
const CALCULATOR_REVIEW_MONTH: Record<string, string> = {
  "nz-fees-free-2026-completion-cutoff-calculator": "September 2026",
  "au-pension-supplement-overseas-12-week-calculator": "September 2026",
  "california-ab1847-wildfire-forbearance-planner": "September 2026",
};

export function getCalculatorReviewMonth(slug: string): string {
  return CALCULATOR_REVIEW_MONTH[slug] ?? LAST_REVIEWED;
}

// Default educational disclaimer rendered under every blog post. A post may
// override it via its optional `disclaimer` field (used when a topic needs
// domain-specific wording, e.g. insurance advice caveats).
export const DEFAULT_BLOG_DISCLAIMER =
  "All content on FinanceLives is for general educational purposes only and does not constitute financial, tax, investment, or legal advice. Tax rates, contribution limits, and financial regulations change frequently — information on this site may not always reflect the most current figures. Always verify with official government sources or consult a qualified financial or tax professional before making any financial decisions.";

// "How we keep this accurate" — the documented editorial/update process shown
// site-wide beside content. Every statement here must remain literally true.
export const EDITORIAL = {
  // One-line trust statement rendered near the bottom of content pages.
  updateStatement:
    "Statutory rules are linked where available; examples and projections use stated assumptions. Report a suspected error for review.",
  // Bullet points for the author page / policy sections.
  process: [
    "We prioritize government and regulator documents for statutory rules and link page-specific sources when available.",
    "Calculator scenarios state assumptions; revised engines are checked with normal and boundary inputs and official examples where available.",
    "Review labels describe documented checks, not a guarantee that no law or product term has changed since then.",
    "Readers can report a suspected error by email; confirmed corrections are documented and updated.",
  ],
  reviewedByName: SITE_AUTHOR.name,
  reviewedByUrl: SITE_AUTHOR.url,
};
